import React, { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";
import { MiniLeadForm } from "./MiniLeadForm";

/**
 * QcAuditPopup (Section 7)
 *
 * Homepage-only modal that fires 5 seconds after the visitor lands, ONCE
 * per browser (guarded by localStorage). Closeable via the X button or
 * by clicking outside the panel. Dark theme, mobile responsive, no
 * em-dashes or en-dashes in the copy.
 *
 * The form inside is <MiniLeadForm variant="popup" />, which:
 *   1. Opens WhatsApp SYNCHRONOUSLY on submit (primary action).
 *   2. POSTs the record to /api/leads with lead_type="qc-audit" and
 *      source="qc-audit-popup".
 */

const STORAGE_KEY = "pickquick_qc_audit_popup_seen";
const DELAY_MS = 5000;

/**
 * We only show the popup once the visitor has both:
 *   (a) waited at least DELAY_MS since page load (a floor, so it never
 *       fires on a fast scroll past the hero),
 *   (b) actually scrolled past the hero section.
 *
 * (b) is a real intent signal, (a) is a courtesy floor. localStorage still
 * gates the popup to first visit only.
 */

const heroScrolledPast = () => {
  if (typeof document === "undefined") return true;
  const hero = document.querySelector(".hero-section");
  if (!hero) return true; // If we can't find the hero, be permissive.
  const rect = hero.getBoundingClientRect();
  // Bottom of hero is above the viewport top => visitor has scrolled past.
  return rect.bottom <= 0;
};

export const QcAuditPopup = () => {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    // We do not re-set the flag here; it was set on first mount so the
    // modal never re-appears in this browser regardless of how it closed.
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let seen = null;
    try {
      seen = window.localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      // localStorage may throw in private-browsing / storage-quota edge
      // cases. In that case we silently skip the popup for this session.
      console.warn("[qc-audit-popup] localStorage unavailable:", err);
      return undefined;
    }
    if (seen) return undefined;

    const loadedAt = Date.now();
    let fired = false;

    const persistSeen = () => {
      try {
        window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
      } catch (err) {
        console.warn("[qc-audit-popup] cannot persist seen flag:", err);
      }
    };

    const tryFire = () => {
      if (fired) return;
      // Gate on both: (a) 5s floor since load, (b) scrolled past hero.
      if (Date.now() - loadedAt < DELAY_MS) return;
      if (!heroScrolledPast()) return;
      fired = true;
      setOpen(true);
      persistSeen();
      window.removeEventListener("scroll", onScroll);
    };

    const onScroll = () => tryFire();
    window.addEventListener("scroll", onScroll, { passive: true });
    // Re-check when the delay floor elapses in case the visitor has
    // already scrolled past the hero by then.
    const floorTimer = window.setTimeout(tryFire, DELAY_MS + 50);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(floorTimer);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    // Prevent body scroll while the modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      data-testid="qc-audit-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="qc-audit-popup-title"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        backgroundColor: "rgba(0, 0, 0, 0.72)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        overflowY: "auto",
      }}
    >
      <div
        // Click inside the panel must not close the modal.
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "560px",
          borderRadius: "18px",
          background: "linear-gradient(135deg, rgba(241,89,43,0.10) 0%, rgba(0,0,0,0) 55%), #0F0F0F",
          border: "1px solid #262626",
          padding: "36px 28px 28px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
        }}
      >
        <button
          type="button"
          onClick={close}
          data-testid="qc-audit-popup-close"
          aria-label="Close"
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: "36px",
            height: "36px",
            borderRadius: "999px",
            background: "transparent",
            border: "1px solid #262626",
            color: "#B8B8B8",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <X className="w-4 h-4" />
        </button>

        <p
          style={{
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#F1592B",
            marginBottom: "10px",
          }}
        >
          Free
        </p>
        <h2
          id="qc-audit-popup-title"
          data-testid="qc-audit-popup-title"
          className="text-white"
          style={{ fontSize: "26px", fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.01em", marginBottom: "10px" }}
        >
          Free QC <span style={{ color: "#F1592B" }}>audit</span>: see where your brand is leaking revenue
        </h2>
        <p style={{ color: "#B8B8B8", fontSize: "15px", lineHeight: 1.55, marginBottom: "22px" }}>
          One platform, one category, no cost. We show you the stockouts, price gaps, and lost search positions before you commit to anything.
        </p>

        <MiniLeadForm
          source="qc-audit-popup"
          variant="popup"
          pagePath="/"
          onDone={close}
        />
      </div>
    </div>
  );
};

export default QcAuditPopup;
